import { Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";

export const AboutSheet = ({ className }: { className?: string }) => {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="secondary"
          size="sm"
          className={`glass-card hover:scale-105 smooth-transition ${className || ""}`}
        >
          <Info className="h-4 w-4 mr-2" />
          About
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>About</SheetTitle>
          <SheetDescription>
            The story behind Sky Lofi ATC
          </SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-8rem)] mt-6 pr-4">
          <div className="space-y-6 text-sm">
            <section>
              <h3 className="text-lg font-semibold mb-3">Why This Exists</h3>
              <p className="text-muted-foreground leading-relaxed">
                Remember lofiatc.com? That beautiful site where you could study to lofi beats 
                and live ATC radio? Yeah, it went dark in 2024 and I wasn't okay with that.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-2">
                So I rebuilt it. But better.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-3">What You're Getting</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                <strong>Sky Lofi ATC</strong> mixes live air traffic control radio from real airports with 
                lofi hip hop, but with a twist: gorgeous video backgrounds of planes in flight. 
                It's like having a window seat while you work.
              </p>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Current Features</h4>
                  <ul className="space-y-1 text-muted-foreground">
                    <li>✈️ Multiple ATC frequencies to choose from</li>
                    <li>✈️ Cinematic airplane video backgrounds</li>
                    <li>✈️ Auto-monitoring (checks streams every 4 hours so you don't get dead air)</li>
                    <li>✈️ Zero ads, zero tracking, zero BS</li>
                    <li>✈️ Clean, modern design that doesn't get in your way</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Coming Soon</h4>
                  <ul className="space-y-1 text-muted-foreground">
                    <li>🚀 Live airport webcam backgrounds</li>
                    <li>🚀 Custom sound effects (rain, cabin noise, etc.)</li>
                    <li>🚀 More music genres (jazz, ambient, classical)</li>
                    <li>🚀 Save your favorite presets</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-3">The Tech</h3>
              <p className="text-muted-foreground leading-relaxed">
                I look for ATC feeds from various sources, automatically verify they're working 
                every 4 hours, mix them with music in real-time, and serve it all through a slick 
                interface with video backgrounds. It's not rocket science, but it does cost money 
                to run.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-3">Support the Project</h3>
              <p className="text-muted-foreground leading-relaxed">
                This is free and ad-free forever. But servers and bandwidth aren't. If you 
                use this regularly and can spare a few bucks,{" "}
                <a 
                  href="https://buymeacoffee.com/skylofiatc" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  buymeacoffee.com/skylofiatc
                </a>
                . It genuinely helps keep this thing alive.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold mb-3">Got Ideas?</h3>
              <p className="text-muted-foreground leading-relaxed">
                I'm one person building this for the community. If you have airport requests, 
                feature ideas, or just want to say hi, fill out the contact form and I'll get 
                back to you.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Clear skies ✈️
              </p>
            </section>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
